import { createContext, useContext, type ReactNode } from "react";
import { useDragSelection, type UseDragSelectionReturn } from "@/hooks/useDragSelection";

const DragSelectionContext = createContext<UseDragSelectionReturn | null>(null);

interface DragSelectionProviderProps {
  children: ReactNode;
  /** Filter function to determine if a cell is selectable */
  isSelectable?: (rowId: string, cellKey: number) => boolean;
}

/**
 * Provider component that enables bulk cell selection across multiple rows.
 * Wrap your table/grid container with this provider to enable drag selection.
 */
export function DragSelectionProvider({ 
  children, 
  isSelectable,
}: DragSelectionProviderProps) {
  const dragSelection = useDragSelection({
    isSelectable,
  });

  return (
    <DragSelectionContext.Provider value={dragSelection}>
      {children}
    </DragSelectionContext.Provider>
  );
}

/**
 * Hook to access drag selection context. Must be used within DragSelectionProvider.
 * @throws Error if used outside of DragSelectionProvider
 */
export function useDragSelectionContext(): UseDragSelectionReturn {
  const context = useContext(DragSelectionContext);
  if (!context) {
    throw new Error("useDragSelectionContext must be used within a DragSelectionProvider");
  }
  return context;
}

/**
 * Hook to optionally access drag selection context.
 * Returns null if used outside of DragSelectionProvider.
 */
export function useDragSelectionContextOptional(): UseDragSelectionReturn | null {
  return useContext(DragSelectionContext);
}
